import React, { useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import ProfileEdit from "./ProfileEdit";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AOS from "aos";
import "aos/dist/aos.css";
import api from "../utils/api";
import { Link } from "react-router-dom";
import LoyaltyWalletSettings from "./LoyaltyWalletSettings";
import PosDisplaySettings from "./PosDisplaySettings";
import SecuritySettings from "./SecuritySettings";
import { useAuth } from "./authentication/AuthContext";
import QRCode from "qrcode.react";
import { 
  Store, 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  User, 
  QrCode, 
  Edit3,
  CreditCard,
  Copy,
  Download,
  ShieldCheck,
  Shield
} from "lucide-react";

const Profile = () => {
  const { isStaff } = useAuth();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [adminDetails, setAdminDetails] = useState({});
  const [editDetails, setEditDetails] = useState({});
  const [userId, setUserId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [image, setImage] = useState("");
  const [logoImage, setLogoImage] = useState("");
  const [faviconImage, setFaviconImage] = useState("");
  const [slug, setSlug] = useState("");

  const handleImageChange = (e) => {
    setImage(e.target.files[0]);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Logo size must be less than 2MB");
        return;
      }
      setLogoImage(file);
    }
  };

  const handleFaviconChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 500 * 1024) {
        toast.error("Favicon size must be less than 500KB");
        return;
      }
      setFaviconImage(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoImage(null);
    setEditDetails((prev) => ({ ...prev, restaurantLogo: "" }));
  };

  const handleRemoveFavicon = () => {
    setFaviconImage(null);
    setEditDetails((prev) => ({ ...prev, restaurantFavicon: "" }));
  };

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      mirror: false,
    });
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        if (decodedToken.user) {
          const uId = decodedToken.user.id;
          const uSlug = decodedToken.user.slug;
          setUserId(uId);
          setSlug(uSlug);

          if (uId) {
            api
              .get(`user/${uId}`)
              .then((response) => {
                setAdminDetails(response.data);
              })
              .catch((error) => {
                console.error("Error fetching user data:", error);
              });
          }
        }
      } catch (err) {
        console.error("Failed to decode token:", err);
      }
    }
  }, []);

  const handleEditClick = () => {
    setEditDetails({ ...adminDetails });
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setEditDetails({ ...adminDetails });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const inputValue = type === "checkbox" ? checked : value;
    const keys = name.split(".");
    setEditDetails((prevDetails) => {
      if (keys.length > 1) {
        return {
          ...prevDetails,
          [keys[0]]: {
            ...prevDetails[keys[0]],
            [keys[1]]: inputValue,
          },
        };
      } else {
        return { ...prevDetails, [name]: inputValue };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Token not found. Please log in.");
      }

      let secureUrl = editDetails.qrCodeImageUrl;
      let secureLogoUrl = editDetails.restaurantLogo || "";
      let secureFaviconUrl = editDetails.restaurantFavicon || "";

      // S3 uploader using backend proxy to bypass CORS
      const uploadToS3 = async (fileObj) => {
        const formData = new FormData();
        formData.append("image", fileObj);
        // You can optionally append folder here if you want to override default
        
        const res = await api.post("/s3/upload", formData);

        return res.data.fileUrl;
      };

      if (image) {
        secureUrl = await uploadToS3(image);
      }
      if (logoImage) {
        secureLogoUrl = await uploadToS3(logoImage);
      }
      if (faviconImage) {
        secureFaviconUrl = await uploadToS3(faviconImage);
      }

      const updatedAdminDetails = {
        ...editDetails,
        qrCodeImageUrl: secureUrl,
        restaurantLogo: secureLogoUrl,
        restaurantFavicon: secureFaviconUrl,
      };

      await api.put(`user/${userId}`, updatedAdminDetails, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setAdminDetails(updatedAdminDetails);
      setIsPopupOpen(false);
      toast.success("Profile details updated successfully!");
    } catch (error) {
      console.error("Error updating user data:", error);
      toast.error("Error updating profile details");
    }
    setIsSubmitting(false);
  };

  // Construct menu link
  const menuLink = slug ? `${window.location.origin}/#/r/${slug}/menu` : "";

  const handleCopyLink = () => {
    if (!menuLink) return;
    navigator.clipboard.writeText(menuLink);
    toast.success("Customer Menu Link copied to clipboard!");
  };

  const handleDownloadQR = () => {
    const canvas = document.getElementById("profile-qr-code");
    if (!canvas) return;
    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");
    let downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = `${adminDetails.shop_type || "menu"}-qr.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    toast.success("QR Code downloaded successfully!");
  };

  const toggleAcceptingOrders = async () => {
    try {
      const token = localStorage.getItem("token");
      const newValue = !adminDetails.isAcceptingOrders;
      await api.put(`user/${userId}`, { isAcceptingOrders: newValue }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminDetails(prev => ({ ...prev, isAcceptingOrders: newValue }));
      toast.success(newValue ? "Now accepting digital orders!" : "Kitchen paused: Not accepting new orders.");
    } catch (err) {
      toast.error("Failed to update kitchen status.");
      console.error(err);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50 pt-20 pb-12 px-4 md:px-8">
      <div className={`max-w-7xl mx-auto space-y-6 transition duration-300 ${isPopupOpen ? "blur-md pointer-events-none" : ""}`}>
        
        {/* Profile Header Block */}
        <div className="bg-white rounded-3xl p-6 md:p-8 border border-slate-200/60 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-teal-50 border border-teal-200 flex items-center justify-center text-teal-600 shadow-inner overflow-hidden relative group">
              {adminDetails.restaurantLogo ? (
                <img src={adminDetails.restaurantLogo} alt="Logo" className="w-full h-full object-contain bg-white" />
              ) : (
                <Store className="w-8 h-8" />
              )}
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-3xl font-black text-slate-800 tracking-tight">
                  {adminDetails.shop_type || "Restaurant Profile"}
                </h1>
                <span className="text-[10px] font-black uppercase tracking-wider bg-teal-50 text-teal-600 border border-teal-200 px-2.5 py-1 rounded-full flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5" /> Active Merchant
                </span>
              </div>
              <p className="text-sm text-slate-500 font-medium mt-1 flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-slate-400" /> {adminDetails.address || "No Address Configured"}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {slug && (
              <Link
                to={`/dashboard/qr/${slug}`}
                className="flex items-center gap-2 bg-slate-900 hover:bg-black text-white px-5 py-3 rounded-2xl shadow-sm text-sm font-black transition duration-200"
              >
                <QrCode className="w-4 h-4" /> QR Poster Designer
              </Link>
            )}

            <a
              href="#security"
              className="flex items-center gap-2 bg-white border border-slate-200 hover:border-slate-350 text-slate-700 hover:text-slate-950 px-5 py-3 rounded-2xl shadow-sm text-sm font-black transition duration-200"
            >
              <Shield className="w-4 h-4 text-amber-500" /> Security & Devices
            </a>

            <button
              onClick={handleEditClick}
              className="flex items-center gap-2 bg-white border border-slate-200 hover:border-slate-350 text-slate-700 hover:text-slate-950 px-5 py-3 rounded-2xl shadow-sm text-sm font-black transition duration-200"
            >
              <Edit3 className="w-4 h-4 text-slate-500" /> Edit Settings
            </button>
          </div>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          
          {/* Card 1: Restaurant Details */}
          <div className="bg-white rounded-3xl p-6 border border-slate-200/60 shadow-sm space-y-4" data-aos="fade-up">
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2 border-b border-slate-100 pb-3">
              <Store className="w-5 h-5 text-teal-600" /> Outlet Information
            </h3>
            <div className="space-y-3.5">
              <div>
                <span className="text-[10px] uppercase font-black tracking-wider text-slate-400">Trading Name</span>
                <p className="text-sm font-bold text-slate-800 mt-0.5">{adminDetails.shop_type || "N/A"}</p>
              </div>
              <div>
                <span className="text-[10px] uppercase font-black tracking-wider text-slate-400">Street Address</span>
                <p className="text-sm font-bold text-slate-800 mt-0.5">{adminDetails.address || "N/A"}</p>
              </div>
              <div>
                <span className="text-[10px] uppercase font-black tracking-wider text-slate-400">Registered QR Link Slug</span>
                <p className="text-xs font-mono font-bold text-teal-650 bg-teal-50 border border-teal-100/80 px-2 py-1.5 rounded-lg w-fit mt-1">
                  {slug || "N/A"}
                </p>
              </div>
              
              {adminDetails.restaurantFavicon && (
                <div className="pt-2 border-t border-slate-100">
                  <span className="text-[10px] uppercase font-black tracking-wider text-slate-400">Favicon Logo</span>
                  <div className="mt-1">
                    <img src={adminDetails.restaurantFavicon} alt="Favicon" className="w-8 h-8 object-contain rounded shadow-sm border border-slate-200 bg-white" />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Card 2: Contact Details */}
          <div className="bg-white rounded-3xl p-6 border border-slate-200/60 shadow-sm space-y-4" data-aos="fade-up" data-aos-delay="100">
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2 border-b border-slate-100 pb-3">
              <User className="w-5 h-5 text-teal-600" /> Merchant Contact
            </h3>
            <div className="space-y-3.5">
              <div>
                <span className="text-[10px] uppercase font-black tracking-wider text-slate-400">Primary Owner</span>
                <p className="text-sm font-bold text-slate-800 mt-0.5">{adminDetails.owner || "N/A"}</p>
              </div>
              <div>
                <span className="text-[10px] uppercase font-black tracking-wider text-slate-400">Login Mobile Number</span>
                <p className="text-sm font-bold text-slate-800 mt-0.5 flex items-center gap-1.5">
                  <Phone className="w-4 h-4 text-slate-400" /> {adminDetails.mobile || "N/A"}
                </p>
              </div>
              <div>
                <span className="text-[10px] uppercase font-black tracking-wider text-slate-400">Public Contact Number</span>
                {adminDetails.publicMobile ? (
                  <p className="text-sm font-bold text-teal-700 mt-0.5 flex items-center gap-1.5">
                    <Phone className="w-4 h-4 text-teal-400" /> {adminDetails.publicMobile}
                    <span className="text-[10px] font-black bg-teal-50 text-teal-600 border border-teal-100 px-2 py-0.5 rounded-full ml-1">Shown on receipts</span>
                  </p>
                ) : (
                  <p className="text-xs font-semibold text-slate-400 mt-0.5 flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5 text-slate-300" />
                    <span>Not set — login number <strong className="text-slate-600">{adminDetails.mobile}</strong> will be used</span>
                  </p>
                )}
              </div>
              <div>
                <span className="text-[10px] uppercase font-black tracking-wider text-slate-400">Email Address</span>
                <p className="text-sm font-bold text-slate-800 mt-0.5 flex items-center gap-1.5">
                  <Mail className="w-4 h-4 text-slate-400" /> {adminDetails.email || "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Card 3: Payments & Operational Settings */}
          <div className="bg-white rounded-3xl p-6 border border-slate-200/60 shadow-sm space-y-4" data-aos="fade-up" data-aos-delay="200">
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2 border-b border-slate-100 pb-3">
              <CreditCard className="w-5 h-5 text-teal-600" /> Operational & Billing
            </h3>
            <div className="space-y-4">
              
              {/* Emergency Kitchen Toggle */}
              <div className="bg-slate-50 border border-slate-200/50 rounded-xl p-3 flex items-center justify-between">
                <div>
                  <span className="text-[11px] uppercase font-black tracking-wider text-slate-500 block">Digital Orders</span>
                  <p className={`text-sm font-bold mt-0.5 ${adminDetails.isAcceptingOrders !== false ? "text-emerald-600" : "text-red-500"}`}>
                    {adminDetails.isAcceptingOrders !== false ? "Accepting Orders" : "Kitchen Paused"}
                  </p>
                </div>
                <button
                  onClick={toggleAcceptingOrders}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${
                    adminDetails.isAcceptingOrders !== false ? 'bg-teal-500' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      adminDetails.isAcceptingOrders !== false ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div>
                <span className="text-[10px] uppercase font-black tracking-wider text-slate-400">UPI Address for QR Payments</span>
                <p className="text-sm font-mono font-bold text-slate-800 mt-0.5 bg-slate-50 border border-slate-200/50 p-2 rounded-xl">
                  {adminDetails.upiId || "UPI ID Not Set"}
                </p>
              </div>

              <div>
                <span className="text-[10px] uppercase font-black tracking-wider text-slate-400 block mb-1.5">Service Times</span>
                <div className="space-y-1.5">
                  <div className="flex justify-between items-center text-xs font-semibold text-slate-600">
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-slate-400" /> Weekdays:</span>
                    <span className="font-bold text-slate-800">{adminDetails.openingHours?.mondayFriday || "Closed / Not Set"}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs font-semibold text-slate-600">
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-slate-400" /> Weekends:</span>
                    <span className="font-bold text-slate-800">{adminDetails.openingHours?.saturdaySunday || "Closed / Not Set"}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Live Customer Menu QR Code Card */}
        {slug && (
          <div className="bg-white rounded-3xl p-6 border border-slate-200/60 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6" data-aos="fade-up">
            <div className="space-y-3.5 flex-1">
              <div>
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <QrCode className="w-5 h-5 text-teal-600" /> Live Customer Menu QR
                </h3>
                <p className="text-xs text-slate-500 mt-1 font-medium">
                  Customers can scan this QR code to view your real-time menu card, customize items, and place direct or dine-in orders.
                </p>
              </div>

              <div className="bg-slate-50 border border-slate-200/50 rounded-2xl p-3 flex items-center justify-between gap-4">
                <span className="text-xs font-mono font-bold text-slate-600 truncate">
                  {menuLink}
                </span>
                <button
                  onClick={handleCopyLink}
                  className="p-2 bg-white hover:bg-slate-100 rounded-xl text-slate-600 hover:text-slate-900 border border-slate-200 transition shadow-sm"
                  title="Copy link"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-5 border border-slate-100 rounded-2xl p-4 bg-slate-50/50">
              <div className="bg-white p-3 rounded-2xl border border-slate-200/60 shadow-inner">
                <QRCode
                  id="profile-qr-code"
                  value={menuLink}
                  size={120}
                  level="H"
                  includeMargin={true}
                />
              </div>
              <div className="space-y-2 text-center sm:text-left">
                <h4 className="text-sm font-bold text-slate-800">Scan or Download</h4>
                <p className="text-[11px] text-slate-400 font-semibold max-w-[150px]">
                  Print this QR to place on your tables or at takeaway counters.
                </p>
                <button
                  onClick={handleDownloadQR}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-900 hover:bg-black text-white rounded-xl text-xs font-black transition duration-200 shadow-sm"
                >
                  <Download className="w-3.5 h-3.5" /> Download PNG
                </button>
              </div>
            </div>
          </div>
        )}

        {/* POS Display & Countertop Privacy Settings */}
        {userId && (
          <div data-aos="fade-up" data-aos-delay="100" className="pt-2">
            <PosDisplaySettings
              userId={userId}
              settings={adminDetails}
              onUpdate={(updated) =>
                setAdminDetails((prev) => ({ ...prev, ...updated }))
              }
            />
          </div>
        )}

        {/* Loyalty Wallet Integration */}
        {userId && (
          <div data-aos="fade-up" data-aos-delay="150" className="pt-2">
            <LoyaltyWalletSettings
              userId={userId}
              settings={adminDetails}
              onUpdate={(updated) =>
                setAdminDetails((prev) => ({ ...prev, ...updated }))
              }
            />
          </div>
        )}

        {/* Security & Active Devices Management — owner only (server enforces the same) */}
        {userId && !isStaff && (
          <div data-aos="fade-up" data-aos-delay="200" className="pt-2">
            <SecuritySettings />
          </div>
        )}

      </div>

      {/* Edit Form Modal */}
      <ProfileEdit
        isOpen={isPopupOpen}
        onClose={handleClosePopup}
        onSubmit={handleSubmit}
        editDetails={editDetails}
        handleInputChange={handleInputChange}
        onImageChange={handleImageChange}
        onLogoChange={handleLogoChange}
        onFaviconChange={handleFaviconChange}
        logoImage={logoImage}
        faviconImage={faviconImage}
        onRemoveLogo={handleRemoveLogo}
        onRemoveFavicon={handleRemoveFavicon}
        isSubmitting={isSubmitting}
      />

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default Profile;
